import {
  hasCompleteQueryData,
  throwOnQueryError,
} from 'ZalamoLibrary/src/lib/core/graphql/complete-query-data';

import {
  Subject,
  debounceTime,
  filter,
  map,
  pipe,
  switchMap,
  takeUntil,
  tap,
  throttleTime,
} from 'rxjs';

import {
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { TypedFormBuilder } from 'projects/zalamo-library/src/lib/ui/form/TypedFormBuilder';
import { NgControl } from '@angular/forms';
import { AbstractValueAccessor } from 'ZalamoLibrary/src/lib/core/form/FormControl';
import { ProtectionWatermarkConfig } from 'graphql/apps/photographer/src/graphql';
import { ZalamoDialogService } from 'ZalamoLibrary/src/lib/ui/zalamo-dialog/zalamo-dialog.service';
import { WaterManagementComponent } from '../water-management/water-management.component';
import {
  GetMineFileWatermarksGQL,
  ProtectionWatermarkSource,
  ZalamoObject,
} from '../../../../graphql';
import { ZalamoTooltipService } from 'ZalamoLibrary/src/lib/ui/zalamo-tooltip/zalamo-tooltip.service';

import { WatermarkPreviewComponent } from '../watermark-preview/watermark-preview.component';
import { TooltipRef } from 'ZalamoLibrary/src/lib/ui/zalamo-tooltip/tooltip-ref';
import { OrganizationService } from 'ZalamoLibrary/src/lib/core/organizations/organization.service';
import { VisualGuideManagerService } from 'ZalamoLibrary/src/lib/ui/visual-guide/visual-guide-manager.service';
import { translationProvider } from '@ui/nls/translations/translate.directive';
import { Required } from 'ZalamoLibrary/src/lib/core/decorators/Required';

const VISUAL_GUIDE_ID = 'WatermarkOnlineEditor';

@Component({
  selector: 'app-watermark-inline-picker',
  templateUrl: './watermark-inline-picker.component.html',
  styleUrls: ['./watermark-inline-picker.component.scss'],
  providers: [translationProvider('publicSessionEditor')],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class WatermarkInlinePickerComponent
  extends AbstractValueAccessor
  implements OnInit, OnDestroy
{
  model = this.fb.typedGroup<ProtectionWatermarkConfig>({
    mode: ['Multi'],
    linesOpacity: [0.8],
    showLines: [true],
    textOpacity: [0.8],
    text: [[]],
    source: [ProtectionWatermarkSource.Text],
    fileName: [''],
    customConfig: [null],
    aiLevel: ['1'],
  });
  @Input() type = 1;
  @ViewChild('editor', { static: false }) editorElement: ElementRef<any>;
  showPreview$ = new Subject();
  availableWatermarks$ = this.organizationService.getCurrentOrganization().pipe(
    switchMap((organizationId) =>
      this.getMineFileWatermarksGQL
        .watch({ variables: { organizationId } })
        .valueChanges.pipe(
          throwOnQueryError,
          filter(hasCompleteQueryData),
          map((result) => result.data['availableWatermarkFiles']),
          map((watermarks) =>
            watermarks.filter(({ type }) => type === this.type),
          ),
        ),
    ),
  );
  tooltipRef: TooltipRef<WatermarkPreviewComponent> = null;
  private advancedOpen = false;
  @Input() @Required context: ZalamoObject;
  constructor(
    private fb: TypedFormBuilder,
    private zalamoDialogService: ZalamoDialogService,
    private tooltipService: ZalamoTooltipService,
    private el: ElementRef,
    public organizationService: OrganizationService,
    public getMineFileWatermarksGQL: GetMineFileWatermarksGQL,
    private visualGuideManagerService: VisualGuideManagerService,
    @Self() @Optional() public ngControl: NgControl,
  ) {
    super();
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  openAdvanced() {
    this.advancedOpen = true;
    const ref = this.zalamoDialogService.openRight(WaterManagementComponent);
    this.updateRef(ref);
    ref.componentInstance.context = this.context;
    ref.componentInstance.saved
      .pipe(takeUntil(ref.afterClosed()))
      .subscribe((config) => {
        ref.close();
        this.model.patchValue(config);
        this.advancedOpen = false;
      });
  }

  hideOnBlur() {
    if (this.tooltipRef !== null) {
      this.tooltipRef.close();
    }
  }

  ngOnInit(): void {
    console.log(this.editorElement);
    this.visualGuideManagerService.checkForGuide(
      VISUAL_GUIDE_ID,
      this.el.nativeElement,
    );
    let fromUpdate = false;
    this.subscribeWhileAlive(
      this.model.valueChanges.pipe(
        filter(() => !this.advancedOpen && !fromUpdate),
        throttleTime(5000),
        tap(() => {
          this.tooltipRef = this.tooltipService.open(
            WatermarkPreviewComponent,
            'above',
            this.editorElement.nativeElement,
            { hasArrow: true },
          );
        }),
        debounceTime(10000),
        tap(() => {
          this.tooltipRef.close();
          this.tooltipRef = null;
        }),
      ),
      this.valueSubject.pipe(
        takeUntil(this.model.valueChanges),
        tap((current) => {
          fromUpdate = true;
          this.model.patchValue(current, { emitEvent: false });
          fromUpdate = false;
        }),
      ),
      this.model.valueChanges.pipe(
        tap((current) => {
          console.log(current);
          this.writeValue(current);
          if (this.tooltipRef) {
            this.updateRef(this.tooltipRef);
          }
        }),
      ),
    );
  }

  updateRef(ref: any) {
    ref.componentInstance.text = this.model.value.text;
    ref.componentInstance.mode = this.model.value.mode;
    ref.componentInstance.showLines = this.model.value.showLines;
    ref.componentInstance.opacity = this.model.value.textOpacity;
    ref.componentInstance.lineOpacity = this.model.value.linesOpacity;
    ref.componentInstance.source = this.model.value.source;
    ref.componentInstance.fileName = this.model.value.fileName;
    ref.componentInstance.customConfig = this.model.value.customConfig;
    ref.componentInstance.aiLevel = this.model.value.aiLevel;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.visualGuideManagerService.removeGuide(VISUAL_GUIDE_ID);
  }
}
